// src/components/dashboards/nlqCsv.ts
// CSV download helpers for NL-query results. The CSV is built SERVER-SIDE and export-scoped
// (see downloadNlCsvExportScoped); the old client-side (columns, rows) → CSV builder was
// removed (066 red-team) because it serialized VIEW-scope rows, bypassing the download scope.

function triggerCsvDownload(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Download an NL-query result as CSV through the EXPORT-scope-gated server endpoint
 * (066 B / Sitting 3.1). The on-screen result is on the VIEW scope; this re-runs the same
 * question server-side restricted to the user's EXPORT scope, so a consortium member can't
 * mass-download another institution's patient rows. Returns how many patients were excluded
 * so the caller can warn. Triggers the browser save when there is anything to download.
 */
export async function downloadNlCsvExportScoped(params: {
  question: string;
  sessionId?: string;
  environmentIds?: number[];
  directive?: unknown;
}): Promise<{ excludedCount: number; includedCount: number; hadRows: boolean }> {
  // Imported lazily so this util stays usable outside the app shell / in tests.
  const { default: api } = await import("../../api/api");
  const body: Record<string, unknown> = { question: params.question };
  if (params.sessionId) body.session_id = params.sessionId;
  if (params.environmentIds && params.environmentIds.length > 0) {
    body.environment_ids = params.environmentIds;
  }
  if (params.directive) body.directive = params.directive;

  const resp = await api.post("/insights/nl-query/csv/", body);
  const csv: string = resp.data?.csv ?? "";
  const excludedCount: number = resp.data?.excluded_patient_count ?? 0;
  const includedCount: number = resp.data?.included_patient_count ?? 0;
  const hadRows = Boolean(csv) && (resp.data?.row_count ?? 0) > 0;
  if (hadRows) {
    triggerCsvDownload(safeCsvFilename(params.question), csv);
  }
  return { excludedCount, includedCount, hadRows };
}

export function safeCsvFilename(question: string | undefined | null): string {
  const stem = (question || "result")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 48) || "result";
  const stamp = new Date().toISOString().slice(0, 10);
  return `oncocraft_${stem}_${stamp}.csv`;
}
